import { fmtUsd, fmtPrice, CHAIN_COLORS } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Target, ShieldWarning, Clock, XCircle, ArrowUp, ArrowDown, Pulse, Receipt, Prohibit } from "@phosphor-icons/react";

function timeAgo(ts) {
  const s = (Date.now() - new Date(ts).getTime()) / 1000;
  if (s < 60) return `${Math.floor(s)}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  return `${Math.floor(s / 3600)}h`;
}

function PositionCard({ p, onClose, closing }) {
  const pnl = p.unrealized_pnl ?? 0;
  const pos = pnl >= 0;
  const tpProgress = Math.min(100, Math.max(0, (pnl / p.tp_target_usd) * 100));
  const slProgress = Math.min(100, Math.max(0, (-pnl / p.stop_loss_usd) * 100));
  const pending = p.status === "pending";
  const isClosing = p.status === "closing";
  const busy = pending || isClosing;
  return (
    <div className={`border p-2.5 flex flex-col gap-2 fade-up ${busy ? "border-yellow-500/40" : "border-[#27272a]"}`} data-testid={`position-${p.symbol}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <span className="font-mono text-sm font-semibold">{p.symbol}</span>
          {p.mode === "live"
            ? <span className="text-[8px] font-mono px-1 py-0.5 bg-red-600 text-white">LIVE</span>
            : <span className="text-[8px] font-mono px-1 py-0.5 border border-[#3f3f46] text-[#a1a1aa]">PAPER</span>}
          {busy && (
            <span className="text-[8px] font-mono px-1 py-0.5 bg-yellow-600 text-black flex items-center gap-0.5" data-testid={`position-state-${p.symbol}`}>
              <span className="h-1 w-1 rounded-full bg-black pulse-dot" />{pending ? "CONFIRMING" : "CLOSING"}
            </span>
          )}
          <span className="text-[9px] font-mono px-1 py-0.5 border" style={{ borderColor: (CHAIN_COLORS[p.chain] || "#3f3f46") + "66", color: CHAIN_COLORS[p.chain] }}>{p.chain}</span>
        </div>
        <span className={`font-mono text-sm font-semibold ${pos ? "text-green-400" : "text-red-400"}`}>{pos ? "+" : ""}{fmtUsd(pnl)}</span>
      </div>
      <div className="flex justify-between text-[10px] font-mono text-[#a1a1aa]">
        <span>entry {fmtPrice(p.entry_price)}</span>
        <span>now {fmtPrice(p.current_price)}</span>
        <span>{fmtUsd(p.size_usd)}</span>
      </div>
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-1.5">
          <Target size={11} className="text-green-500" />
          <div className="flex-1 h-1 bg-[#27272a]"><div className="h-full bg-green-500" style={{ width: `${tpProgress}%` }} /></div>
          <span className="text-[9px] font-mono text-[#a1a1aa]">${p.tp_target_usd}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <ShieldWarning size={11} className="text-red-500" />
          <div className="flex-1 h-1 bg-[#27272a]"><div className="h-full bg-red-500" style={{ width: `${slProgress}%` }} /></div>
          <span className="text-[9px] font-mono text-[#a1a1aa]">${p.stop_loss_usd}</span>
        </div>
      </div>
      <Button data-testid={`close-position-${p.symbol}`} size="sm" onClick={() => onClose(p.id)} disabled={closing || busy}
        className="rounded-none h-6 bg-[#27272a] hover:bg-red-600 hover:text-white text-[#f4f4f5] text-[10px] active:scale-95 disabled:opacity-40">
        {pending ? "Confirming on-chain…" : isClosing ? "Closing on-chain…" : "Close Position"}
      </Button>
      {p.explorer && (
        <a href={p.explorer} target="_blank" rel="noreferrer"
          className="text-[9px] font-mono text-cyan-400 hover:underline text-center">view tx ↗</a>
      )}
    </div>
  );
}

function SectionHeader({ icon, title, count }) {
  return (
    <div className="flex items-center justify-between mb-2">
      <h3 className="font-head text-xs uppercase tracking-widest text-[#a1a1aa] flex items-center gap-1.5">{icon}{title}</h3>
      {count != null && <span className="text-[10px] font-mono text-[#52525b]">{count}</span>}
    </div>
  );
}

export default function RightPanel({ positions, executions, rejected, onClose, closingId }) {
  return (
    <aside className="col-span-12 lg:col-span-3 overflow-y-auto p-4 flex flex-col gap-6" data-testid="right-panel">
      <div>
        <SectionHeader icon={<Pulse size={13} weight="bold" className="text-cyan-400" />} title="Active Positions" count={positions.length} />
        <div className="flex flex-col gap-2" data-testid="active-positions">
          {positions.length === 0 && <p className="text-[11px] text-[#52525b] font-mono py-3">No open positions.</p>}
          {positions.map((p) => <PositionCard key={p.id} p={p} onClose={onClose} closing={closingId === p.id} />)}
        </div>
      </div>

      <div>
        <SectionHeader icon={<Receipt size={13} weight="bold" className="text-cyan-400" />} title="Recent Executions" count={executions.length} />
        <div className="flex flex-col" data-testid="executions-feed">
          {executions.length === 0 && <p className="text-[11px] text-[#52525b] font-mono py-3">No executions yet.</p>}
          {executions.slice(0, 12).map((e) => {
            const buy = e.type === "BUY";
            return (
              <div key={e.id} className="flex items-center justify-between py-1.5 border-b border-[#1f1f22] text-xs">
                <span className="flex items-center gap-1.5">
                  {buy ? <ArrowUp size={12} className="text-cyan-400" /> : <ArrowDown size={12} className="text-orange-400" />}
                  <span className="font-mono font-medium">{e.symbol}</span>
                  <span className="text-[9px] text-[#52525b]">{e.reason || e.type}</span>
                </span>
                <span className="flex items-center gap-2">
                  {e.pnl != null && <span className={`font-mono text-[11px] ${e.pnl >= 0 ? "text-green-400" : "text-red-400"}`}>{e.pnl >= 0 ? "+" : ""}{fmtUsd(e.pnl)}</span>}
                  <span className="text-[9px] font-mono text-[#52525b]">{timeAgo(e.ts)}</span>
                </span>
              </div>
            );
          })}
        </div>
      </div>

      <div>
        <SectionHeader icon={<Prohibit size={13} weight="bold" className="text-red-500" />} title="Rejected Signals" count={rejected.length} />
        <div className="flex flex-col gap-1.5" data-testid="rejected-feed">
          {rejected.length === 0 && <p className="text-[11px] text-[#52525b] font-mono py-3">No rejections yet.</p>}
          {rejected.slice(0, 12).map((r) => (
            <div key={r.id} className="border border-[#27272a] px-2 py-1.5">
              <div className="flex items-center justify-between">
                <span className="font-mono text-xs font-medium">{r.symbol}</span>
                <span className="text-[9px] font-mono text-[#52525b]">{r.chain} · {timeAgo(r.ts)}</span>
              </div>
              <div className="flex flex-wrap gap-1 mt-1">
                {(r.reasons || []).slice(0, 3).map((rs, i) => (
                  <span key={i} className="text-[9px] font-mono text-red-400 flex items-center gap-0.5">
                    <XCircle size={9} weight="fill" /> {rs.label}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </aside>
  );
}
